import React from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { formatDistance } from 'date-fns'

import Button from '../../UniformComponents/Button'
import Panel from '../../UniformComponents/Panel'
import Typography from '../../UniformComponents/Typography'
import DeletePresentationModal from './modals/DeletePresentationModal'
import Input from '../form/Input'
import Select from '../form/Select'

const renderSkeleton = () => (
    <div className="presentations__skeleton-grid" aria-busy="true" aria-label="Loading presentations">
        {[0, 1, 2, 3].map((i) => (
            <div key={i} className="presentations__skeleton-card">
                <div className="presentations__skeleton-thumb" />

                <div className="presentations__text-container">
                    <div className="presentations__skeleton-line presentations__skeleton-line--title" />

                    <div className="presentations__skeleton-line presentations__skeleton-line--meta" />

                    <div className="presentations__skeleton-meta-container">
                        <div className="presentations__skeleton-pill" />

                        <div className="presentations__skeleton-history" />
                    </div>
                </div>
            </div>
        ))}
    </div>
)

const renderEmpty = (onNewPresentation) => (
    <Panel cssClass="presentations__empty">
        <div className="presentations__empty-content">
            <Typography type="h3">No presentations yet</Typography>
            <Typography type="p" color="grey">
                Turn your survey results into a shareable deck. Pick an event and its entry survey, and get started.
            </Typography>
            <Button
                cssClass="primary regular"
                icon="fa-light fa-plus"
                label="New presentation"
                onClick={onNewPresentation}
            />
        </div>
    </Panel>
)

const renderList = (presentations, onOpen, userId, handleOpenDeletePresentationModal) => (
    <div className="presentations__grid">
        {presentations.map((presentation, idx) => (
            <Panel
                key={presentation.uuid ?? idx}
                cssClass="presentations__card"
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <div className="presentations__card-thumb">
                        <i className="fa-light fa-presentation-screen" />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', minWidth: 0, width: '100%' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <Typography type="b" cssClass="presentations__card-title">
                                {`${presentation.data.title || 'Untitled presentation'} - ${presentation.eventName}`}
                            </Typography>
                        </div>

                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <div style={{ backgroundColor: '#F7F5FF', borderRadius: '20px', alignItems: 'center', justifyContent: 'center', padding: '4px 10px', display: 'flex', gap: 4, color: '#715AF8' }}>
                                <i className="fal fa-layer-group" />

                                <Typography type="tiny-b">
                                    {`${presentation.data.section.map((section) => section.slide.length).reduce((a, b) => a + b, 0)} slide(s)`}
                                </Typography>
                            </div>

                            {presentation.lastUpdated && (
                                <Typography type="tiny" color="grey">
                                    <i className="fal fa-timer" style={{ marginRight: 4 }}/>

                                    {`Updated ${formatDistance( new Date(presentation.lastUpdated.replace(' ', 'T') + 'Z'), new Date(), { addSuffix: true, includeSeconds: true } )}`}
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <Button
                        cssClass="tertiary regular"
                        icon="fa-light fa-pen-to-square"
                        label="Open"
                        onClick={() => onOpen(presentation)}
                    />

                    {presentation.createdBy === userId && (
                        <Button
                            cssClass="error regular"
                            icon="fa-light fa-trash"
                            label="Delete"
                            onClick={() => handleOpenDeletePresentationModal(presentation.uuid)}
                        />
                    )}
                </div>
            </Panel>
        ))}
    </div>
)

const PresentationsLanding = ({ store, history }) => {
    const {
        filteredPresentations, presentationsLoading, showForm, handleOpenDeletePresentationModal, appStore: {
            userData: { userId },
        },
    } = store

    const handleOpen = (presentation) => {
        if (presentation.uuid) {
            history.push(`/presentations/${presentation.uuid}`)
        }
    }

    return (
        <div>
            <div style={{ width: '100%', padding: 24, backgroundColor: 'white' }}>
                <Typography type="h2">{i18n.t('presentationbuilder')}</Typography>
            </div>

            <div className="presentations">
                <div className="presentations__header">
                    <div className="presentations__heading">
                        <Typography type="h2">Your presentations</Typography>
                        <Typography type="p" color="grey">
                            Pick up where you left off, or start a new presentation.
                            Drafts save automatically as you build
                        </Typography>
                    </div>
                    <Button
                        cssClass="primary regular"
                        icon="fa-light fa-plus"
                        label="New presentation"
                        onClick={showForm}
                    />
                </div>

                {!presentationsLoading && (
                    <Panel style={{ overflow: 'visible' }}>
                        <div style={{ display: 'flex', gap: 20, alignItems: 'center', justifyContent: 'flex-start' }}>
                            <Input
                                label="Search"
                                value={store.presentationsSearch}
                                onChange={store.handlePresentationsFilter}
                                style={{ maxWidth: '300px' }}
                            />

                            <Select
                                label="Events"
                                onBlur={store.handlePresentationsEventFilter}
                                options={store.eventOptions}
                                defaultValue={store.presentationsEventFilter}
                                style={{ width: '100%', maxWidth: '300px', marginTop: 0 }}
                                isClearable
                                blurOnClear
                            />
                        </div>
                    </Panel>
                )}

                {presentationsLoading && renderSkeleton()}
                {!presentationsLoading && filteredPresentations.length === 0 && renderEmpty(showForm)}
                {!presentationsLoading && filteredPresentations.length > 0
                    && renderList(filteredPresentations, handleOpen, userId, handleOpenDeletePresentationModal)}
            </div>

            <DeletePresentationModal store={store} />
        </div>
    )
}

export default observer(PresentationsLanding)
